import './bridge-war-components.scss';
import { Call, Goal, SeatData, goalLabels, goalText, goals } from '../../app/types';
import { IconComponent } from '../icon-component/icon-component';
import { SharkBidComponent } from '../shark-bid-component/shark-bid-component';
import { bridgeWarAssets } from '../../assets/bridgewars';
import { classNames } from '../../utils/mixed';
import { convertStringToBid } from '../../app-interfaces/shark-game-engine-helper';
import { outputActions } from '../../slices/outputSlice';
import { useAppDispatch, useAppSelector } from '../../app/hooks';
import { useTranslation } from 'react-i18next';
import React, { FC, useState } from 'react';

const logoMap: Record<string, string> = {
    bwar_7_: bridgeWarAssets.logos.bridgeWar,
    bwar_7_e_: bridgeWarAssets.logos.bridgeWar,
    bwar_8_: bridgeWarAssets.logos.bridgeWarHeroes,
    bwar_8_e_: bridgeWarAssets.logos.bridgeWarHeroes,
    bwar_c_: bridgeWarAssets.logos.bridgeWarChallenger,
    bwar_ex_: bridgeWarAssets.logos.bridgeWarExtreme,
};

export const BridgeWarLogoComponent: FC = () => {
    const { urlParams } = useAppSelector((state) => state.app);
    const logoPrefix = urlParams?.['mode'] ?? 'bwar_7_';
    const logo = logoMap[logoPrefix] ?? logoMap['bwar_7_'];
    return (
        <div className="BridgeWarLogoComponent">
            <img src={logo} alt="BridgeWar Logo" />
        </div>
    );
};

export const BridgeWarInfosComponent: FC = () => {
    const { t } = useTranslation();
    return (
        <div className="BridgeWarInfosComponent">
            <IconComponent icon="attacker" />
            <div>{t('bridgeWar.info.attacker')}</div>
            <IconComponent icon="defender" />
            <div>{t('bridgeWar.info.defender')}</div>
            <IconComponent icon="compass" />
            <div>{t('bridgeWar.info.compass')}</div>
        </div>
    );
};

export const BridgeWarGameInfoComponent: FC<{ seatData: SeatData }> = ({ seatData }) => {
    const { bridgeWarGameInfo } = useAppSelector((state) => state.table);

    const avatar = seatData?.bridgeWarAvatar ? bridgeWarAssets.avatars[seatData.bridgeWarAvatar] : undefined;

    if (bridgeWarGameInfo == null) {
        return;
    }
    return (
        <div className="BridgeWarGameInfoComponent" style={{ background: avatar?.color ?? 'transparent' }}>
            {bridgeWarGameInfo.map((info) => (
                <div className={`line`} key={info}>
                    {info}
                </div>
            ))}
        </div>
    );
};

export const BridgeWarGameSelectorComponent: FC = () => {
    const dispatch = useAppDispatch();
    const { output_setGameSelector } = outputActions;
    const { bridgeWarGameSelector } = useAppSelector((state) => state.table);
    const {
        sharkLayout: { selectedCardSet },
    } = useAppSelector((state) => state.app);
    const [step, setStep] = useState(0);
    const [trump, setTrump] = useState<Call>();
    const [goal, setGoal] = useState<Goal>();
    const [done, setDone] = useState<boolean>();
    const { title, text, buttonLabelBack, buttonLabelNext } = bridgeWarGameSelector[step];

    function handleBack() {
        setStep((_step) => {
            return _step - 1;
        });
    }

    function handleNext() {
        if (step < bridgeWarGameSelector.length - 1) {
            setStep((_step) => {
                return _step + 1;
            });
        } else {
            dispatch(output_setGameSelector({ goal, trump, done: true }));
            setDone(true);
        }
    }

    function handleTrump(trump: Call) {
        setTrump(trump);
        dispatch(output_setGameSelector({ trump, done: false }));
    }

    const nextDisabled = (step === 1 && !trump) || (step === 2 && !goal);

    if (done) {
        return;
    }

    return (
        <section className="BridgeWarGameSelectorComponent">
            {title && <div className="title" dangerouslySetInnerHTML={{ __html: title }} />}
            {text && <div className="text" dangerouslySetInnerHTML={{ __html: text }} />}
            {step === 1 && (
                <div className="trumps">
                    {['1c', '1d', '1h', '1s', '1nt'].map((_call) => {
                        const call = _call as Call;
                        const bid = convertStringToBid(call);
                        return (
                            <div
                                key={_call}
                                onClick={() => handleTrump(call)}
                                className={classNames('trump', trump === call && 'selected')}
                            >
                                <SharkBidComponent bid={bid} selectedCardSet={selectedCardSet} />
                            </div>
                        );
                    })}
                </div>
            )}
            {step === 2 && goal && (
                <div className="goal-message">
                    {goal === 7 && <span>{goalText[0]}</span>}
                    {goal === 9 && <span>{goalText[1]}</span>}
                    {goal === 12 && <span>{goalText[2]}</span>}
                </div>
            )}
            {step === 2 && (
                <div className="goals">
                    {goals.map((_goal, index) => (
                        <button
                            key={_goal}
                            onClick={() => setGoal(_goal)}
                            className={classNames('goal', goal === _goal && 'selected')}
                        >
                            {goalLabels[index]}
                        </button>
                    ))}
                </div>
            )}

            <div className="buttons">
                {buttonLabelBack && (
                    <button className="buttonLabelBack" onClick={handleBack}>
                        {buttonLabelBack}
                    </button>
                )}
                {buttonLabelNext && (
                    <button className="buttonLabelNext" onClick={handleNext} disabled={nextDisabled}>
                        {buttonLabelNext}
                    </button>
                )}
            </div>
        </section>
    );
};
